import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { seoRoutes } from '../../shared/seo';
import { isExcursionHidden, orderByExcursion } from '../../shared/excursions';
import { InitialDataContext } from '../initialData';
import { Brand } from './Brand';
import { BookingProvider, useBooking } from './BookingProvider';
import { ContestProvider } from './ContestDialog';
import { ContestBurst } from './ContestBurst';
import { RouteHead } from './RouteHead';
import { Analytics } from '../analytics/Analytics';

/** Every tour and audience page and the courses, linked from the footer so each is one click from anywhere. */
const footerPages = seoRoutes.filter((route) => route.kind === 'trip' || route.path === '/excursions/kttu' || route.path === '/kursy' || route.path === '/shkolam');

export function SiteLayout() {
  return <BookingProvider><ContestProvider><PublicLayout /></ContestProvider></BookingProvider>;
}

function invertedScrollPalette() {
  const range = document.documentElement.scrollHeight - window.innerHeight;
  if (range <= 0) return false;
  const progress = Math.min(1, Math.max(0, window.scrollY / range));
  return Math.floor(progress * 5 + 1e-7) % 2 === 1;
}

function PublicLayout() {
  const { openBooking, isBookingOpen, finishDoorOpening } = useBooking();
  const { pathname, hash } = useLocation();
  const initialData = useContext(InitialDataContext);
  const hasGuides = (initialData.guides?.length ?? 0) > 0;
  // Excursions the admin hid (out of season) are not linked; their pages still open from old links. Trips follow the
  // admin's order; the catalog, school and course pages come after them.
  const visibleFooterPages = orderByExcursion(footerPages, initialData.excursions, (route) => route.trip?.excursion ?? '')
    .filter((route) => !(route.trip && isExcursionHidden(initialData.excursions, route.trip.excursion)));
  // Viewport-dependent state starts with the server-rendered value and is corrected after mount;
  // CSS keeps the mobile menu collapsed until then (see `data-menu` in global.css).
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLogoInverted, setIsLogoInverted] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const brandRef = useRef<HTMLDivElement>(null);
  const brandAnchorRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const brand = brandRef.current;
    const anchor = brandAnchorRef.current;
    if (!brand || !anchor) return;
    let frame = 0;
    let landing: DOMRect | null = null;
    let compact: DOMRect;
    let mobile = false;
    let lastProgress = -1;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mix = (start: number, end: number, progress: number) => start + (end - start) * progress;
    const setGeometry = (name: string, value: number, unit = 'px') => brand!.style.setProperty(name, `${value}${unit}`);
    function paint() {
      frame = 0;
      if (!landing) return;
      const rawProgress = Math.min(1, Math.max(0, window.scrollY / 280));
      const progress = reducedMotion.matches ? (rawProgress < .5 ? 0 : 1) : rawProgress;
      if (progress === 1 && lastProgress === 1) return;
      lastProgress = progress;
      const largeDoor = Math.min(landing.width * .72, landing.height * .65, 280);
      const smallDoor = mobile ? 44 : 64;
      const largeFont = Math.min(landing.width / 4.9, 86);
      const smallFont = mobile ? 14.4 : 21.6;
      const largeWordTop = (landing.height + largeDoor) / 2 - largeFont * .35;
      setGeometry('--brand-progress', progress, '');
      brand!.toggleAttribute('data-compact', progress >= .5);
      setGeometry('--brand-left', mix(landing.left, compact.left, progress));
      setGeometry('--brand-top', mix(landing.top - window.scrollY, compact.top, progress));
      setGeometry('--brand-width', mix(landing.width, compact.width, progress));
      setGeometry('--brand-height', mix(landing.height, compact.height, progress));
      setGeometry('--brand-door-size', mix(largeDoor, smallDoor, progress));
      setGeometry('--brand-mark-size', mix(largeDoor * .9, mobile ? 32 : 54, progress));
      setGeometry('--brand-door-left', mix((landing.width - largeDoor) / 2, 0, progress));
      setGeometry('--brand-door-top', mix(Math.max(0, (landing.height - largeDoor) / 2 - largeFont), 0, progress));
      setGeometry('--brand-word-left', mix(landing.width / 2, smallDoor + (mobile ? 5 : 9), progress));
      setGeometry('--brand-word-top', mix(largeWordTop, (smallDoor - smallFont * 1.15) / 2, progress));
      setGeometry('--brand-word-size', mix(largeFont, smallFont, progress));
      setGeometry('--brand-word-offset', mix(-50, 0, progress), '%');
    }
    const measure = () => {
      const slot = document.getElementById('landing-brand-slot');
      brand.classList.toggle('brand-booking--landing', Boolean(slot));
      if (!slot) {
        landing = null;
        brand.removeAttribute('style');
        brand.removeAttribute('data-compact');
        return;
      }
      const bounds = slot.getBoundingClientRect();
      landing = new DOMRect(bounds.left, bounds.top + window.scrollY, bounds.width, bounds.height);
      compact = anchor.getBoundingClientRect();
      mobile = window.matchMedia('(max-width: 850px)').matches;
      lastProgress = -1;
      paint();
    };
    function schedule() {
      if (!frame) frame = window.requestAnimationFrame(paint);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(anchor);
    const slot = document.getElementById('landing-brand-slot');
    if (slot) observer.observe(slot);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure);
    reducedMotion.addEventListener('change', measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
      reducedMotion.removeEventListener('change', measure);
      window.cancelAnimationFrame(frame);
      brand.classList.remove('brand-booking--landing');
      brand.removeAttribute('style');
      brand.removeAttribute('data-compact');
    };
  }, [pathname]);

  useLayoutEffect(() => {
    setIsMenuOpen(false);
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' });
    else {
      let id = hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* keep the raw id */ }
      if (id !== 'request') document.getElementById(id)?.scrollIntoView({ behavior: 'instant' });
    }
  }, [pathname, hash]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 850px)');
    function updateViewport() {
      setIsMobile(media.matches);
      setIsMenuOpen(false);
    }
    updateViewport();
    media.addEventListener('change', updateViewport);
    return () => media.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    let frame = 0;
    let previous = invertedScrollPalette();
    setIsLogoInverted(previous);
    function updateScroll() {
      frame = 0;
      const next = invertedScrollPalette();
      if (next !== previous) {
        previous = next;
        setIsLogoInverted(next);
      }
    }
    function scheduleScroll() {
      if (!frame) frame = window.requestAnimationFrame(updateScroll);
    }
    window.addEventListener('scroll', scheduleScroll, { passive: true });
    window.addEventListener('resize', scheduleScroll);
    const observer = new ResizeObserver(scheduleScroll);
    if (headerRef.current?.parentElement) observer.observe(headerRef.current.parentElement);
    scheduleScroll();
    return () => {
      window.removeEventListener('scroll', scheduleScroll);
      window.removeEventListener('resize', scheduleScroll);
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    function closeOnNavigation() { setIsMenuOpen(false); }
    window.addEventListener('hashchange', closeOnNavigation);
    return () => window.removeEventListener('hashchange', closeOnNavigation);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    }
    function closeOutside(event: PointerEvent) {
      if (event.target instanceof Node && !headerRef.current?.contains(event.target)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [isMenuOpen]);

  function closeMenu() { setIsMenuOpen(false); }
  function toggleMenu() { setIsMenuOpen(current => !current); }
  function showBooking() {
    closeMenu();
    openBooking();
  }
  return (
    <div className="site-shell">
      <a className="skip-link" href="#content">К содержанию</a>
      <RouteHead />
      <Analytics />
      <header ref={headerRef} className={`site-header${isLogoInverted ? ' site-header--inverted' : ''}`}>
        <div className="header-inner">
          <div ref={brandAnchorRef} className="header-brand-anchor">
            <div ref={brandRef} className="brand-booking">
              <Brand interactive doorOpen={isBookingOpen} onDoorClick={showBooking} onDoorOpened={finishDoorOpening} />
            </div>
          </div>
          <button className="mobile-booking" type="button" aria-label="Оставить заявку" aria-haspopup="dialog" onClick={showBooking}>Записаться</button>
          <button ref={menuButtonRef} className="mobile-menu-toggle" type="button" aria-label={isMenuOpen ? 'Закрыть навигацию' : 'Открыть навигацию'} aria-expanded={isMenuOpen} aria-controls="site-navigation" onClick={toggleMenu}>
            <span aria-hidden="true" /><span aria-hidden="true" /><span aria-hidden="true" />
          </button>
          <nav id="site-navigation" className="site-nav" aria-label="Основная навигация" data-menu={isMenuOpen ? 'open' : 'closed'} hidden={isMobile && !isMenuOpen} onClick={closeMenu}>
            <Link to="/">Главная</Link>
            <a href="/#directions">Экскурсии</a>
            <Link to="/kursy">Курсы и лекции</Link>
            <Link to="/shkolam">Школам</Link>
            {hasGuides && <a href="/#guides">Изнанка в лицах</a>}
            <Link to="/partners">Партнёрам</Link>
            <a className="mobile-news-link" href="/#news">Новости</a>
            <button className="desktop-booking" type="button" aria-haspopup="dialog" onClick={showBooking}>Записаться</button>
          </nav>
        </div>
        <svg className="header-wave" viewBox="0 0 1440 64" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path d="M0 0H1440V8C1180 4 1050 8 900 20S590 35 420 50S130 62 0 32Z" fill="currentColor" />
        </svg>
      </header>
      <main id="content"><Outlet /></main>
      <footer className="site-footer">
        <div className="container footer-grid">
          <Brand compact />
          <div><p>ИП Постовой Константин Андреевич</p><p>ИНН 234904741004</p></div>
          <nav className="footer-links" aria-label="Экскурсии и курсы">{visibleFooterPages.map((route) => <Link key={route.path} to={route.path}>{route.breadcrumbs.at(-1)!.name}</Link>)}</nav>
          <div className="footer-links"><Link to="/privacy">Политика обработки данных</Link><a href="tel:+79086935909">8 (908) 693-59-09</a><a href="https://t.me/krd_iznanka" target="_blank" rel="noreferrer">Написать в Telegram</a><a href="https://max.ru/u/f9LHodD0cOL8_Fj35RGhuyLiOq6qCp7gWf2nDYLUYDw854d6hI6P_2QzJHQ" target="_blank" rel="noreferrer">Написать в MAX</a></div>
        </div>
      </footer>
      <ContestBurst />
    </div>
  );
}
